import axios from 'axios';
import { AppointmentData, EasyAppointmentsResponse, AvailabilitySlot, ProviderInfo } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const api = axios.create({
  baseURL: `${API_BASE_URL}/index.php/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer Pr4d0c123@'
  },
});

const findOrCreateCustomer = async (data: AppointmentData): Promise<number> => {
  try {
    const searchResponse = await api.get('/customers', {
      params: {
        q: data.phone,
        sort: '-id',
      },
    });

    if (searchResponse.data && Array.isArray(searchResponse.data) && searchResponse.data.length > 0) {
      const existingCustomer = searchResponse.data.find(
        (customer: any) => customer.phone === data.phone
      );
      if (existingCustomer) {
        return existingCustomer.id;
      }
    }
  } catch (error) {
    console.log('Cliente não encontrado, criando novo...');
  }

  const customerData = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email || `${data.phone}@noemail.com`,
    phone: data.phone,
    timezone: 'America/Sao_Paulo',
    language: 'portuguese-br',
  };

  const response = await api.post('/customers', customerData);
  return response.data.id;
};

export const createAppointment = async (
  data: AppointmentData
): Promise<EasyAppointmentsResponse> => {
  try {
    const customerId = await findOrCreateCustomer(data);
    
    const startDatetime = `${data.date} ${data.time}:00`;
    
    const startDate = new Date(`${data.date}T${data.time}:00`);
    const endDate = new Date(startDate.getTime() + 30 * 60000);
    const endDatetime = `${data.date} ${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}:00`;
    
    const appointmentData = {
      book: new Date().toISOString().slice(0, 19).replace('T', ' '),
      start: startDatetime,
      end: endDatetime,
      serviceId: 1,
      providerId: 2,
      customerId: customerId,
    };

    const response = await api.post('/appointments', appointmentData);
    
    return {
      success: true,
      appointmentId: response.data.id,
    };
  } catch (error) {
    console.error('Erro ao criar agendamento:', error);
    
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Erro ao criar agendamento. Tente novamente.',
        };
      } else if (error.request) {
        return {
          success: false,
          message: 'Não foi possível conectar ao servidor. Verifique sua conexão.',
        };
      }
    }
    
    return {
      success: false,
      message: 'Erro ao criar agendamento. Tente novamente.',
    };
  }
};

// Função para processar os horários disponíveis
export const getAvailableSlots = async (serviceId: number, date: string, providerId: number = 2): Promise<string[]> => {
  try {
    const response = await api.get('/availabilities', {
      params: {
        serviceId,
        date,
        providerId,
      },
    });

    console.log('Resposta da API de disponibilidade:', response.data);

    // A API retorna um array de strings simples como ['15:00', '15:15', ...]
    if (Array.isArray(response.data)) {
      // Filtrar valores válidos (apenas strings) e apenas horários de 30 em 30 minutos
      const slots = response.data
        .filter((slot: any) => typeof slot === 'string' && slot)
        .filter((slot: string) => {
          // Extrair os minutos do horário
          const minutes = slot.split(':')[1];
          // Aceitar apenas :00 e :30
          return minutes === '00' || minutes === '30';
        });
      console.log('Horários extraídos (30 em 30 min):', slots);
      return slots;
    }

    // Se a resposta for um objeto com start/end
    if (typeof response.data === 'object' && response.data !== null && !Array.isArray(response.data)) {
      // Verificar se tem propriedade 'start' ou similar
      if (response.data.start && typeof response.data.start === 'string') {
        const minutes = response.data.start.split(':')[1];
        if (minutes === '00' || minutes === '30') {
          return [response.data.start];
        }
        return [];
      }
      
      // Tentar extrair arrays do objeto
      const arrays = Object.values(response.data).filter(value => Array.isArray(value));
      if (arrays.length > 0) {
        const firstArray = arrays[0] as any[];
        const slots = firstArray
          .map((slot: any) => {
            if (typeof slot === 'string') return slot;
            if (slot && typeof slot === 'object' && slot.start) return slot.start;
            return null;
          })
          .filter((slot): slot is string => slot !== null && typeof slot === 'string')
          .filter((slot: string) => {
            // Extrair os minutos do horário
            const minutes = slot.split(':')[1];
            // Aceitar apenas :00 e :30
            return minutes === '00' || minutes === '30';
          });
        console.log('Horários extraídos do objeto (30 em 30 min):', slots);
        return slots;
      }
    }

    console.warn('Formato de resposta não reconhecido:', response.data);
    return [];
  } catch (error) {
    console.error('Erro ao buscar horários disponíveis:', error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('Detalhes do erro:', error.response.data);
    }
    return [];
  }
};

// Nova função para buscar informações do provider
export const getProviderInfo = async (providerId: number = 2): Promise<ProviderInfo | null> => {
  try {
    const response = await api.get(`/providers/${providerId}`);
    console.log('Informações do provider:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar informações do profissional:', error);
    return null;
  }
};

export const getServices = async () => {
  try {
    const response = await api.get('/services');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar serviços:', error);
    return [];
  }
};

export const getProviders = async () => {
  try {
    const response = await api.get('/providers');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar profissionais:', error);
    return [];
  }
};